import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import Footer from '../components/Footer';
import '../style.css';
import '../nov.css';

const CopyrightReport = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    copyrightOwner: '',
    workTitle: '',
    workDescription: '',
    originalContentUrl: '',
    violationUrl: '',
    violationDescription: '',
    evidence: '',
    declaration: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      if (!db) {
        console.log('[local dev] Copyright report skipped:', formData);
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          copyrightOwner: '',
          workTitle: '',
          workDescription: '',
          originalContentUrl: '',
          violationUrl: '',
          violationDescription: '',
          evidence: '',
          declaration: false
        });
        setTimeout(() => setSubmitStatus(null), 5000);
        return;
      }

      await addDoc(collection(db, 'copyright-violations'), {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        copyrightOwner: formData.copyrightOwner,
        workTitle: formData.workTitle,
        workDescription: formData.workDescription,
        originalContentUrl: formData.originalContentUrl,
        violationUrl: formData.violationUrl,
        violationDescription: formData.violationDescription,
        evidence: formData.evidence,
        declaration: formData.declaration,
        timestamp: serverTimestamp(),
        status: 'new'
      });

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        copyrightOwner: '',
        workTitle: '',
        workDescription: '',
        originalContentUrl: '',
        violationUrl: '',
        violationDescription: '',
        evidence: '',
        declaration: false
      });
      
      // 5 saniye sonra success mesajını temizle
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);

    } catch (error) {
      console.error('Copyright violation report submission error:', error);
      setSubmitStatus('error');
      
      // 5 saniye sonra error mesajını temizle
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section id="copyright" className="min-h-screen bg-[#f5f5f7] py-32 lg:py-48 relative overflow-hidden">
        <div className="container mx-auto px-6 lg:px-16 max-w-[1400px] relative z-10">
          <div className="w-full flex flex-col lg:flex-row items-center lg:items-start justify-between gap-16 lg:gap-24">
          
          {/* Sol Taraf - Header */}
          <div className="lg:w-5/12 text-center lg:text-left space-y-10">
            <motion.div 
              className="inline-block"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span 
                className="text-xs font-medium badge-gradient text-gray-700 py-2.5 px-5 rounded-full inline-block uppercase tracking-wide" 
                style={{ fontFamily: 'aesthet-nova' }}
              >
                Copyright Violation Report
              </span>
            </motion.div>

            <motion.h1 
              className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tight uppercase"
              style={{ fontFamily: 'aesthet-nova' }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <span style={{ color: 'rgb(70, 52, 135)' }}>
                COPYRIGHT
              </span>
              <br/>
              <span 
                style={{ '--shimmer-width': '250px' }} 
                className="text-neutral-600/50 animate-shimmer bg-clip-text bg-no-repeat [background-position:0_0] [background-size:var(--shimmer-width)_100%] [transition:background-position_1s_cubic-bezier(.6,.6,0,1)_infinite] bg-gradient-to-r from-neutral-100 via-black/80 via-50% to-neutral-100"
              >
                VIOLATION
              </span>
              <br/>
              <span style={{ color: 'rgb(70, 52, 135)' }}>
                REPORT
              </span>
            </motion.h1>

            <motion.p 
              className="text-lg leading-relaxed text-gray-600"
              style={{ fontFamily: 'aesthet-nova' }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              If you believe your copyright has been violated, please fill out the form below. We will review and respond to your report as soon as possible.
            </motion.p>
          </div>

          {/* Sağ Taraf - Form and Info */}
          <div className="lg:w-7/12 flex flex-col gap-12">
            
            {/* Form */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <form onSubmit={handleSubmit} className="bg-white p-8 rounded-3xl">
                
                {/* Name and Email */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-black mb-2">Full Name *</label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-3xl text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9BCFFF] focus:border-transparent"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-black mb-2">Email Address *</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-3xl text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9BCFFF] focus:border-transparent"
                      placeholder="Enter your email address"
                    />
                  </div>
                </div>

                {/* Phone and Copyright Owner */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-black mb-2">Phone Number</label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-3xl text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9BCFFF] focus:border-transparent"
                      placeholder="Enter your phone number"
                    />
                  </div>
                  <div>
                    <label htmlFor="copyrightOwner" className="block text-sm font-semibold text-black mb-2">Copyright Owner *</label>
                    <input
                      id="copyrightOwner"
                      name="copyrightOwner"
                      type="text"
                      required
                      value={formData.copyrightOwner}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-3xl text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9BCFFF] focus:border-transparent"
                      placeholder="Name of copyright owner"
                    />
                  </div>
                </div>

                {/* Work Title */}
                <div className="mb-6">
                  <label htmlFor="workTitle" className="block text-sm font-semibold text-black mb-2">Title of Copyrighted Work *</label>
                  <input
                    id="workTitle"
                    name="workTitle"
                    type="text"
                    required
                    value={formData.workTitle}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9BCFFF] focus:border-transparent"
                    placeholder="Enter the title of your copyrighted work"
                  />
                </div>

                {/* Work Description */}
                <div className="mb-6">
                  <label htmlFor="workDescription" className="block text-sm font-semibold text-black mb-2">Description of Copyrighted Work *</label>
                  <textarea
                    id="workDescription"
                    name="workDescription"
                    required
                    rows="4"
                    value={formData.workDescription}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-3xl text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9BCFFF] focus:border-transparent resize-vertical"
                    placeholder="Describe your copyrighted work in detail"
                  ></textarea>
                </div>

                {/* Original Content URL */}
                <div className="mb-6">
                  <label htmlFor="originalContentUrl" className="block text-sm font-semibold text-black mb-2">Original Content URL</label>
                  <input
                    id="originalContentUrl"
                    name="originalContentUrl"
                    type="url"
                    value={formData.originalContentUrl}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9BCFFF] focus:border-transparent"
                    placeholder="https://example.com/your-original-content"
                  />
                </div>

                {/* Violation URL */}
                <div className="mb-6">
                  <label htmlFor="violationUrl" className="block text-sm font-semibold text-black mb-2">URL of Alleged Violation *</label>
                  <input
                    id="violationUrl"
                    name="violationUrl"
                    type="url"
                    required
                    value={formData.violationUrl}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9BCFFF] focus:border-transparent"
                    placeholder="https://example.com/infringing-content"
                  />
                </div>

                {/* Violation Description */}
                <div className="mb-6">
                  <label htmlFor="violationDescription" className="block text-sm font-semibold text-black mb-2">Description of Violation *</label>
                  <textarea
                    id="violationDescription"
                    name="violationDescription"
                    required
                    rows="4"
                    value={formData.violationDescription}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-3xl text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9BCFFF] focus:border-transparent resize-vertical"
                    placeholder="Explain how your copyright has been violated"
                  ></textarea>
                </div>

                {/* Evidence */}
                <div className="mb-6">
                  <label htmlFor="evidence" className="block text-sm font-semibold text-black mb-2">Supporting Evidence</label>
                  <textarea
                    id="evidence"
                    name="evidence"
                    rows="3"
                    value={formData.evidence}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-3xl text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9BCFFF] focus:border-transparent resize-vertical"
                    placeholder="Provide any additional evidence or documentation"
                  ></textarea>
                </div>

                {/* Declaration Checkbox */}
                <div className="mb-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="declaration"
                      required
                      checked={formData.declaration}
                      onChange={handleChange}
                      className="mt-1 w-5 h-5 text-[#9BCFFF] bg-white border-gray-300 rounded focus:ring-[#9BCFFF] focus:ring-2"
                    />
                    <span className="text-sm text-gray-700">
                      I declare under penalty of perjury that the information in this notification is accurate and that I am the copyright owner or am authorized to act on behalf of the owner of an exclusive right that is allegedly infringed.
                    </span>
                  </label>
                </div>

                {/* Success/Error Messages */}
                {submitStatus === 'success' && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-green-100 border border-green-400 text-green-700 rounded-3xl"
                  >
                    ✅ Your copyright violation report has been submitted successfully! We'll review it and take appropriate action.
                  </motion.div>
                )}
                
                {submitStatus === 'error' && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-3xl"
                  >
                    ❌ There was an error submitting your report. Please try again.
                  </motion.div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={!formData.declaration || isSubmitting}
                  className="w-full bg-gradient-to-r from-[#9BCFFF] via-[#FCD4FE] to-[#B6F1F6] text-black font-semibold py-4 px-6 rounded-3xl hover:shadow-lg transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Copyright Violation Report'}
                </button>
              </form>
            </motion.div>

            {/* Important Information */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="bg-white p-6 rounded-3xl">
                <h3 className="text-2xl font-semibold mb-4 text-black">Important Information</h3>
                <ul className="space-y-4 text-gray-600">
                  <li className="flex items-start gap-2">
                    <svg className="w-6 h-6 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <p>Your copyright violation report constitutes a legal declaration.</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-6 h-6 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <p>False declarations may have legal consequences.</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-6 h-6 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <p>Reports are reviewed within 48 hours.</p>
                  </li>
                </ul>
              </div>
            </motion.div>

          </div>
          
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </>
  );
};

export default CopyrightReport;
